
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { WESTFIELD_POD_ID, londonDateLabel, londonTimeLabel } from "@/lib/reservations";
import ReservePod from "./pages/ReservePod";
import ReservationsAdmin from "./pages/ReservationsAdmin";

import Home from "./pages/Home";

import Partner from "./pages/Partner";
import Design from "./pages/Design";
import Spots from "./pages/Spots";
import SuggestedSpots from "./pages/SuggestedSpots";
import Sandbox from "./pages/Sandbox";
import Pods from "./pages/Pods";
import PodAdd from "./pages/PodAdd";
import ImageTest from "./pages/ImageTest";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PartnerAd from "./pages/PartnerAd";
import PartnerAd2 from "./pages/PartnerAd2";
import PartnerAd3 from "./pages/PartnerAd3";
import PartnerAd4 from "./pages/PartnerAd4";
import PartnerAd5 from "./pages/PartnerAd5";
import Terms from "./pages/Terms";
import AppTest from "./pages/AppTest";

import Projections from "./pages/Projections";
import EndSession from "./pages/EndSession";
import NewHome from "./pages/NewHome";
import Provision from "./pages/Session";
import PodDetailDemo from "./pages/PodDetailDemo";
import ActiveSession from "./pages/ActiveSession";
import Deck from "./pages/Deck";
import DeckView from "./pages/DeckView";
import DeckInfo from "./pages/DeckInfo";
import Internal from "./pages/Internal";
import TechSpecs from "./pages/TechSpecs";
import StPancrasSpecs from "./pages/StPancrasSpecs";
import DeckPrint from "./pages/DeckPrint";
import FlourishProposal from "./pages/FlourishProposal";
import FlourishPrint from "./pages/FlourishPrint";
import FlourishDeck from "./pages/FlourishDeck";
import ShoppertainmentProposal from "./pages/ShoppertainmentProposal";
import ForumRcpProposal from "./pages/ForumRcpProposal";
import NetworkRailProposal from "./pages/NetworkRailProposal";
import InvestorDeck from "./pages/InvestorDeck";
import FundraisingDeck from "./pages/FundraisingDeck";
import DeckAirports from "./pages/DeckAirports";
import DraftEmail from "./pages/DraftEmail";
import Brand from "./pages/Brand";
import AllHands from "./pages/AllHands";
import SpotsMap from "./pages/SpotsMap";
import AddSpot from "./pages/AddSpot";
import SpotsManagement from "./pages/SpotsManagement";
import SuggestSpot from "./pages/SuggestSpot";
import SuggestionsAdmin from "./pages/SuggestionsAdmin";
import SpotsAd from "./pages/SpotsAd";
import CollectionView from "./pages/CollectionView";
import Carousel from "./pages/Carousel";
import Support from "./pages/Support";
import PodPartner from "./pages/PodPartner";
import Demo from "./pages/Demo";
import ProvisionPreview from "./pages/ProvisionPreview";
import Ad from "./pages/Ad";
import Feedback from "./pages/Feedback";
import FeedbackResponses from "./pages/FeedbackResponses";
import GooglePlacesScript from "./components/GooglePlacesScript";
import TempOutage from "./pages/TempOutage";
import Reserved from "./pages/Reserved";
import OffsiteDemo from "./pages/offsite-demo/OffsiteDemo";
import OffsiteAds from "./pages/offsite-demo/OffsiteAds";
import OffsiteLayout from "./pages/offsite-demo/OffsiteLayout";
import OffsitePlans from "./pages/offsite-demo/OffsitePlans";
import OffsitePlan from "./pages/offsite-demo/OffsitePlan";
import Veya from "./pages/veya/Veya";
import { VeyaHowItWorks, VeyaTripCheck, VeyaMonitoring, VeyaFaq } from "./pages/veya/VeyaSubPages";



// Create a client
const queryClient = new QueryClient();

// Component for immediate redirect
const SpotsRedirect = () => {
  useEffect(() => {
    window.location.href = "https://tally.so/r/wkxqgj";
  }, []);

  return null;
};

// WhatsApp redirect to partner page
const WhatsAppRedirect = () => {
  useEffect(() => {
    window.location.href = "/partner";
  }, []);

  return null;
};

// WhatsApp Community redirect
const WhatsAppCommunityRedirect = () => {
  useEffect(() => {
    window.location.href = "https://linktr.ee/qwip.co.uk";
  }, []);

  return null;
};

// Linktree redirect
const LinktreeRedirect = () => {
  useEffect(() => {
    window.location.href = "https://linktr.ee/qwip.co.uk";
  }, []);

  return null;
};

// Temporary reservation windows (London time). Add entries as { from, to, date, fromLabel, toLabel }.
// Times are "YYYY-MM-DD HH:mm" local to Europe/London.
const RESERVED_WINDOWS = [
  { from: "2026-09-21 18:00", to: "2026-09-21 19:30", date: "21 September", fromLabel: "6 PM", toLabel: "7:30 PM" },
];

const londonNow = (): { date: string; minutes: number } => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    minutes: Number(get("hour")) * 60 + Number(get("minute")),
  };
};

// Booking redirect
// Flip to false to resume pay-as-you-go booking (and the /reserved window checks below).
// While on, walk-up booking links go to advance reservations on /reserve-pod.
const BOOKING_OUTAGE_MODE = true;

const BookingRedirect = () => {
  useEffect(() => {
    const podUrl = "https://qwip.co.uk/pod/68cec744-34ac-4ec6-af36-0af781e19d04";

    if (BOOKING_OUTAGE_MODE) {
      window.location.href = "/reserve-pod";
      return;
    }



    const reservedUrl = (date: string, from: string, to: string) =>
      `/reserved?date=${encodeURIComponent(date)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;

    const run = async () => {
      // 1. Manual windows kept in the list above.
      const now = londonNow();
      const match = RESERVED_WINDOWS.find((w) => {
        const [fromDate, fromTime] = w.from.split(" ");
        const [toDate, toTime] = w.to.split(" ");
        const [fh, fm] = fromTime.split(":").map(Number);
        const [th, tm] = toTime.split(":").map(Number);
        return (
          now.date >= fromDate &&
          now.date <= toDate &&
          now.minutes >= fh * 60 + fm &&
          now.minutes < th * 60 + tm
        );
      });

      if (match) {
        window.location.href = reservedUrl(match.date, match.fromLabel, match.toLabel);
        return;
      }

      // 2. Reservations paid for on /reserve-pod.
      try {
        const nowIso = new Date().toISOString();
        const { data } = await supabase.rpc("get_reserved_slots", {
          _pod_id: WESTFIELD_POD_ID,
          _from: nowIso,
          _to: nowIso,
        });
        const slot = (data ?? [])[0];
        if (slot) {
          const start = new Date(slot.start_at);
          const end = new Date(slot.end_at);
          window.location.href = reservedUrl(
            londonDateLabel(start).replace(/^\w+,?\s/, ""),
            londonTimeLabel(start),
            londonTimeLabel(end),
          );
          return;
        }
      } catch {
        // If availability cannot be checked, fall through to normal booking.
      }

      window.location.href = podUrl;
    };

    run();
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
};

// Instagram redirect
const InstagramRedirect = () => {
  useEffect(() => {
    window.location.href = "https://www.instagram.com/qwip.co.uk/";
  }, []);

  return null;
};

// Westfield redirect to Instagram post
const WestfieldRedirect = () => {
  useEffect(() => {
    window.location.href = "https://www.instagram.com/p/DZQJd4TDGpc/?img_index=1";
  }, []);

  return null;
};

// Promo redirect to Instagram reel
const PromoRedirect = () => {
  useEffect(() => {
    window.location.href = "https://www.instagram.com/reel/DTsSUiQkVNA/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==";
  }, []);

  return null;
};

// Video redirect - Instagram reel
const VideoRedirect = () => {
  useEffect(() => {
    window.location.href = "https://www.instagram.com/reel/DTsSUiQkVNA/?utm_source=ig_web_copy_link&igsi=MzRlODBiNWFlZA==";
  }, []);

  return null;
};

// Legacy support group redirect
const SupportGroupRedirect = () => {
  useEffect(() => {
    window.location.href = "https://chat.whatsapp.com/Lx9H5OJpywmCa022EcliOC";
  }, []);

  return null;
};

// KGX redirect to Instagram post
const KgxRedirect = () => {
  useEffect(() => {
    window.location.href = "https://www.instagram.com/p/DUMTtovDML5/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==";
  }, []);

  return null;
};

// Download redirect to App Store
const DownloadRedirect = () => {
  useEffect(() => {
    window.location.href = "https://apps.apple.com/gb/app/qwip/id6761749249";
  }, []);

  return null;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster position="top-right" offset={80} />
      <GooglePlacesScript />
      <Router>
        <Routes>
          <Route path="/" element={<NewHome />} />
          <Route path="/old-home" element={<Home />} />
          <Route path="/kgx" element={<KgxRedirect />} />
          <Route path="/whatsapp" element={<WhatsAppRedirect />} />
          <Route path="/whatsapp-community" element={<WhatsAppCommunityRedirect />} />
          <Route path="/partner" element={<Partner />} />
          <Route path="/partner-ad" element={<PartnerAd />} />
          <Route path="/partner-ad2" element={<PartnerAd2 />} />
          <Route path="/partner-ad3" element={<PartnerAd3 />} />
          <Route path="/partner-ad4" element={<PartnerAd4 />} />
          <Route path="/partner-ad5" element={<PartnerAd5 />} />
          <Route path="/design" element={<Design />} />
          <Route path="/spots" element={<SpotsMap />} />
          <Route path="/spots-archive" element={<SpotsRedirect />} />
          <Route path="/spots-view" element={<Spots />} />
          <Route path="/spots-suggested" element={<SuggestedSpots />} />
          <Route path="/app-test" element={<AppTest />} />
           <Route path="/spots/add" element={<AddSpot />} />
           <Route path="/spots/view" element={<SpotsManagement />} />
           <Route path="/spots/suggest" element={<SuggestSpot />} />
           <Route path="/spots/suggestions" element={<SuggestionsAdmin />} />
           <Route path="/spots-ad" element={<SpotsAd />} />
          <Route path="/collections/:id" element={<CollectionView />} />
          <Route path="/sandbox" element={<Sandbox />} />
          <Route path="/pod/:pod_id" element={<Pods />} />
          <Route path="/pod-add" element={<PodAdd />} />
          <Route path="/image-test" element={<ImageTest />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          
          <Route path="/projections" element={<Projections />} />
          <Route path="/end-session/:sessionId" element={<EndSession />} />
          
          <Route path="/provision" element={<Provision />} />
          <Route path="/session" element={<ActiveSession />} />
          <Route path="/pod-demo/:id" element={<PodDetailDemo />} />
          <Route path="/redirect-1" element={<LinktreeRedirect />} />
          <Route path="/booking-redirect" element={<BookingRedirect />} />
           <Route path="/booking-redirect-1" element={<Navigate to="/reserve-pod" replace />} />
           <Route path="/canvas-redirect" element={<InstagramRedirect />} />
           <Route path="/westfield" element={<WestfieldRedirect />} />
           <Route path="/promo" element={<PromoRedirect />} />
           <Route path="/video" element={<VideoRedirect />} />
          <Route path="/temp-outage" element={<TempOutage />} />
          <Route path="/reserved" element={<Reserved />} />
          <Route path="/support" element={<Support />} />
          <Route path="/support-group" element={<SupportGroupRedirect />} />
          <Route path="/deck" element={<Deck />} />
          <Route path="/deck," element={<Deck />} />
          <Route path="/deck-view" element={<DeckView />} />
          <Route path="/deck-info" element={<DeckInfo />} />
          <Route path="/deck-print" element={<DeckPrint />} />
           <Route path="/investor-deck" element={<InvestorDeck />} />
           <Route path="/deck-feb" element={<FundraisingDeck />} />
          <Route path="/deck-june" element={<FundraisingDeck />} />
          <Route path="/deck-airports" element={<DeckAirports />} />
          <Route path="/flourish" element={<FlourishProposal />} />
          <Route path="/flourish-print" element={<FlourishPrint />} />
           <Route path="/flourish-print-1" element={<FlourishDeck />} />
           <Route path="/shoppertainment" element={<ShoppertainmentProposal />} />
           <Route path="/forumrcp" element={<ForumRcpProposal />} />
            <Route path="/networkrail" element={<NetworkRailProposal />} />
          <Route path="/draft-email" element={<DraftEmail />} />
          <Route path="/internal" element={<Internal />} />
          <Route path="/tech-specs" element={<TechSpecs />} />
          <Route path="/st-pancras-specs" element={<StPancrasSpecs />} />
          <Route path="/brand" element={<Brand />} />
          <Route path="/all-hands" element={<AllHands />} />
           <Route path="/carousel" element={<Carousel />} />
           <Route path="/download" element={<DownloadRedirect />} />
           <Route path="/help" element={<Support />} />
           <Route path="/pod-partner" element={<PodPartner />} />
           <Route path="/demo" element={<Demo />} />
           <Route path="/provision-preview" element={<ProvisionPreview />} />
           <Route path="/ad" element={<Ad />} />
           <Route path="/feedback" element={<Feedback />} />
           <Route path="/reserve-pod" element={<ReservePod />} />
           <Route path="/reserve-pod/admin" element={<ReservationsAdmin />} />
           <Route path="/feedback/responses" element={<FeedbackResponses />} />
          <Route element={<OffsiteLayout />}>
            <Route path="/offsite-demo" element={<OffsiteDemo />} />
            <Route path="/offsite-demo/plans" element={<OffsitePlans />} />
            <Route path="/offsite-demo/plans/:planId" element={<OffsitePlan />} />
          </Route>
          <Route path="/offsite-demo/ads" element={<OffsiteAds />} />
          <Route path="/veya" element={<Veya />} />
          <Route path="/veya-how-it-works" element={<VeyaHowItWorks />} />
          <Route path="/veya-trip-check" element={<VeyaTripCheck />} />
          <Route path="/veya-monitoring" element={<VeyaMonitoring />} />
          <Route path="/veya-faq" element={<VeyaFaq />} />


          
          
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
